import React, { useState, useEffect } from 'react';
import {
  FaSearch,
  FaMapMarkerAlt,
  FaBriefcase,
  FaDollarSign,
  FaClock,
  FaHeart,
  FaShare,
  FaFilter,
  FaTimes,
  FaBuilding,
  FaUserTie,
  FaGraduationCap,
  FaArrowRight,
  FaChevronDown,
  FaCheck,
  FaStar,
  FaEye,
  FaUsers,
  FaCalendarAlt,
  FaPhone,
  FaEnvelope,
  FaLinkedin,
  FaExternalLinkAlt,
  FaChartLine,
  FaHandshake,
  FaRocket,
  FaAward,
  FaTasks,
  FaBalanceScale,
  FaUsersCog
} from 'react-icons/fa';
import './HRJobBoard.css';

const HRJobBoard = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedJob, setSelectedJob] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [savedJobs, setSavedJobs] = useState([]);
  const [filters, setFilters] = useState({
    department: '',
    experience: '',
    salary: '',
    location: '',
    jobType: ''
  });
  const [sortBy, setSortBy] = useState('newest');

  // HR-specific job categories
  const departments = [
    'Talent Acquisition',
    'HR Business Partner',
    'Learning & Development',
    'Compensation & Benefits',
    'HR Analytics',
    'Employee Relations',
    'Diversity & Inclusion',
    'HR Operations'
  ];

  // Professional HR job listings with detailed content
  const jobListings = [
    {
      id: 1,
      title: 'Senior HR Business Partner',
      company: 'TechInnovate Inc.',
      logo: '💼',
      location: 'San Francisco, CA',
      department: 'HR Business Partner',
      experience: 'Senior',
      salary: '$130,000 - $160,000',
      type: 'Full-time',
      posted: '2 hours ago',
      description: 'As a Senior HR Business Partner at TechInnovate, you will serve as a strategic partner to our engineering and product leadership teams. You will drive organizational effectiveness, talent management, and employee engagement initiatives while aligning HR strategies with business objectives in a fast-paced technology environment.',
      overview: 'This role requires a strategic thinker who can balance business partnership with employee advocacy. You will be instrumental in scaling our HR practices as we continue our rapid growth trajectory.',
      responsibilities: [
        'Partner with senior leadership to develop and implement HR strategies that support business objectives',
        'Lead organizational design and workforce planning initiatives',
        'Drive talent management processes including performance management, succession planning, and career development',
        'Provide expert guidance on complex employee relations matters',
        'Analyze HR metrics to identify trends and develop proactive solutions',
        'Coach and mentor managers on leadership best practices and people management'
      ],
      requirements: [
        '8+ years of progressive HR Business Partner experience in technology organizations',
        'Master\'s degree in Human Resources, Organizational Psychology, or related field',
        'Expert knowledge of employment laws and regulations across multiple states',
        'Proven experience scaling HR practices in high-growth environments',
        'Strong analytical skills with experience in HR analytics and metrics',
        'Excellent communication and influencing skills at all organizational levels'
      ],
      qualifications: [
        'SPHR or SHRM-SCP certification preferred',
        'Experience with HRIS systems (Workday, SAP SuccessFactors)',
        'Background in change management and organizational development',
        'Experience in mergers and acquisitions integration'
      ],
      benefits: [
        'Competitive equity package and performance bonuses',
        'Comprehensive health, dental, and vision insurance',
        'Flexible work arrangements and remote work options',
        'Professional development and certification reimbursement',
        '401(k) with 6% company matching',
        'Unlimited PTO and parental leave policies'
      ],
      applications: 18,
      views: 245,
      urgent: true,
      featured: true,
      companyInfo: {
        size: '500-1000 employees',
        industry: 'Enterprise Software',
        founded: 2018,
        mission: 'To revolutionize how businesses leverage artificial intelligence for operational excellence'
      }
    },
    {
      id: 2,
      title: 'Talent Acquisition Manager',
      company: 'GrowthStart Partners',
      logo: '🎯',
      location: 'New York, NY',
      department: 'Talent Acquisition',
      experience: 'Mid-level',
      salary: '$95,000 - $125,000',
      type: 'Full-time',
      posted: '1 day ago',
      description: 'Lead our talent acquisition function and build world-class recruitment strategies to attract top HR professionals. You will manage the full recruitment lifecycle while developing innovative sourcing strategies and enhancing our employer brand in the competitive HR tech market.',
      overview: 'This position offers the opportunity to shape our talent acquisition function and make a significant impact on our organizational growth and culture.',
      responsibilities: [
        'Develop and execute comprehensive talent acquisition strategies for HR roles',
        'Manage end-to-end recruitment process for mid-senior level positions',
        'Build and maintain strong relationships with hiring managers and stakeholders',
        'Implement innovative sourcing strategies to build diverse candidate pipelines',
        'Enhance employer branding through social media and professional networks',
        'Analyze recruitment metrics to optimize processes and improve efficiency'
      ],
      requirements: [
        '5+ years of talent acquisition experience with focus on HR recruitment',
        'Proven track record of hiring for specialized HR positions',
        'Experience with applicant tracking systems (Greenhouse, Lever)',
        'Strong knowledge of employment laws and recruitment best practices',
        'Excellent networking and relationship-building skills',
        'Bachelor\'s degree in Human Resources or related field'
      ],
      qualifications: [
        'Experience in HR technology or consulting environment',
        'Certified Internet Recruiter (CIR) or similar certification',
        'Background in diversity and inclusion recruitment strategies',
        'Familiarity with HR analytics and recruitment metrics'
      ],
      benefits: [
        'Uncapped performance bonuses based on hiring metrics',
        'Comprehensive benefits package including mental health support',
        'Flexible hybrid work model',
        'Annual professional development budget',
        'Stock option program',
        'Wellness stipend and gym membership'
      ],
      applications: 24,
      views: 189,
      urgent: false,
      featured: true,
      companyInfo: {
        size: '200-500 employees',
        industry: 'HR Technology',
        founded: 2020,
        mission: 'To transform HR operations through innovative technology solutions'
      }
    },
    {
      id: 3,
      title: 'Compensation & Benefits Analyst',
      company: 'GlobalEnterprise Corp',
      logo: '💰',
      location: 'Chicago, IL',
      department: 'Compensation & Benefits',
      experience: 'Mid-level',
      salary: '$85,000 - $110,000',
      type: 'Full-time',
      posted: '3 days ago',
      description: 'Join our Total Rewards team to design, implement, and manage competitive compensation and benefits programs. You will conduct market analysis, ensure pay equity, and develop benefits strategies that attract and retain top HR talent while ensuring compliance with regulatory requirements.',
      overview: 'This role is critical in maintaining our competitive position in the market and ensuring fair and equitable compensation practices across the organization.',
      responsibilities: [
        'Conduct market pricing and compensation analysis for HR positions',
        'Administer annual compensation planning and merit increase processes',
        'Manage executive compensation programs and equity administration',
        'Analyze benefits utilization and recommend program enhancements',
        'Ensure compliance with federal and state compensation regulations',
        'Partner with HRBPs on compensation recommendations and issues'
      ],
      requirements: [
        '4+ years of compensation and benefits experience',
        'Strong analytical skills with proficiency in Excel and compensation tools',
        'Knowledge of compensation survey participation and analysis',
        'Understanding of benefits regulations (ERISA, ACA, HIPAA)',
        'Experience with HRIS and compensation management systems',
        'Bachelor\'s degree in Finance, Business, or related field'
      ],
      qualifications: [
        'Certified Compensation Professional (CCP) certification',
        'Experience with Workday Advanced Compensation',
        'Background in public company compensation practices',
        'Knowledge of international compensation principles'
      ],
      benefits: [
        'Comprehensive health insurance with low employee contributions',
        'Performance-based annual bonuses',
        'Professional certification reimbursement',
        'Flexible spending accounts and health savings accounts',
        'Retirement plan with generous company matching',
        'Tuition reimbursement program'
      ],
      applications: 15,
      views: 134,
      urgent: true,
      featured: false,
      companyInfo: {
        size: '5000+ employees',
        industry: 'Financial Services',
        founded: 1995,
        mission: 'To deliver exceptional financial solutions while maintaining the highest standards of integrity'
      }
    },
    {
      id: 4,
      title: 'Learning & Development Specialist',
      company: 'TalentGrowth Academy',
      logo: '📚',
      location: 'Remote',
      department: 'Learning & Development',
      experience: 'Mid-level',
      salary: '$75,000 - $95,000',
      type: 'Full-time',
      posted: '1 week ago',
      description: 'Design and deliver innovative learning solutions that enhance HR professional capabilities across the organization. You will develop curriculum, facilitate workshops, and measure learning effectiveness to drive continuous improvement in HR competencies and leadership development.',
      overview: 'This remote position offers the opportunity to shape the learning culture of our organization and develop HR professionals at all career stages.',
      responsibilities: [
        'Design and develop comprehensive learning programs for HR professionals',
        'Facilitate virtual and in-person training sessions on HR topics',
        'Conduct training needs assessments and gap analysis',
        'Develop e-learning content and digital learning resources',
        'Measure training effectiveness through Kirkpatrick model evaluation',
        'Partner with HR leadership on competency framework development'
      ],
      requirements: [
        '3+ years of learning and development experience',
        'Proven experience designing HR-focused training programs',
        'Strong facilitation and presentation skills',
        'Experience with learning management systems',
        'Knowledge of adult learning principles and instructional design',
        'Bachelor\'s degree in Organizational Development or related field'
      ],
      qualifications: [
        'Certified Professional in Learning and Performance (CPLP)',
        'Experience with e-learning authoring tools (Articulate, Captivate)',
        'Background in leadership development program design',
        'Familiarity with HR certification preparation courses'
      ],
      benefits: [
        'Fully remote work environment with flexible scheduling',
        'Comprehensive health and wellness benefits',
        'Annual learning and development budget',
        'Home office setup stipend',
        'Virtual team building events',
        'Professional association membership fees'
      ],
      applications: 22,
      views: 167,
      urgent: false,
      featured: false,
      companyInfo: {
        size: '100-200 employees',
        industry: 'Professional Services',
        founded: 2015,
        mission: 'To empower organizations through innovative learning and development solutions'
      }
    },
    {
      id: 5,
      title: 'HR Analytics Manager',
      company: 'DataDriven HR Solutions',
      logo: '📊',
      location: 'Austin, TX',
      department: 'HR Analytics',
      experience: 'Senior',
      salary: '$120,000 - $150,000',
      type: 'Full-time',
      posted: '4 days ago',
      description: 'Lead our HR analytics function to transform people data into actionable insights that drive strategic decision-making. You will build predictive models, develop HR metrics dashboards, and partner with business leaders to optimize workforce planning and talent management strategies.',
      overview: 'This role combines deep analytical expertise with strategic HR knowledge to influence organizational decisions through data-driven insights.',
      responsibilities: [
        'Develop and maintain comprehensive HR analytics framework',
        'Build predictive models for turnover, performance, and hiring success',
        'Create interactive dashboards and reports for HR metrics',
        'Partner with business leaders on workforce planning analytics',
        'Conduct advanced statistical analysis on people data',
        'Ensure data governance and privacy compliance in HR analytics'
      ],
      requirements: [
        '6+ years of HR analytics or people analytics experience',
        'Advanced proficiency in SQL, R, Python, or similar analytical tools',
        'Experience with HRIS systems and data visualization tools',
        'Strong statistical analysis and modeling skills',
        'Knowledge of HR processes and metrics',
        'Master\'s degree in Data Science, Statistics, or related field'
      ],
      qualifications: [
        'Experience with machine learning applications in HR',
        'Proficiency in Tableau, Power BI, or similar visualization tools',
        'Background in organizational research methods',
        'Knowledge of GDPR and data privacy regulations'
      ],
      benefits: [
        'Competitive salary with significant bonus potential',
        'Comprehensive health and retirement benefits',
        'Flexible work arrangements and remote options',
        'Annual technology and tooling allowance',
        'Conference and training attendance budget',
        'Research and development time allocation'
      ],
      applications: 12,
      views: 98,
      urgent: false,
      featured: true,
      companyInfo: {
        size: '300-600 employees',
        industry: 'HR Analytics & Technology',
        founded: 2017,
        mission: 'To revolutionize HR decision-making through advanced analytics and artificial intelligence'
      }
    },
    {
      id: 6,
      title: 'Diversity & Inclusion Program Manager',
      company: 'InclusiveWork Inc.',
      logo: '🌍',
      location: 'Boston, MA',
      department: 'Diversity & Inclusion',
      experience: 'Mid-level',
      salary: '$90,000 - $115,000',
      type: 'Full-time',
      posted: '2 days ago',
      description: 'Drive our diversity, equity, and inclusion initiatives to create a more inclusive workplace culture. You will develop D&I strategies, measure program effectiveness, and partner across the organization to embed inclusive practices into all HR processes and business operations.',
      overview: 'This strategic role requires a passionate advocate for inclusion who can translate D&I principles into actionable programs and measurable outcomes.',
      responsibilities: [
        'Develop and implement comprehensive D&I strategy and roadmap',
        'Design and deliver inclusive leadership training programs',
        'Analyze diversity metrics and develop action plans for improvement',
        'Partner with talent acquisition on diverse hiring strategies',
        'Facilitate employee resource groups and inclusion networks',
        'Stay current on D&I best practices and legal requirements'
      ],
      requirements: [
        '4+ years of D&I program management experience',
        'Proven track record of developing successful D&I initiatives',
        'Strong facilitation and training delivery skills',
        'Experience with D&I metrics and measurement',
        'Excellent stakeholder management and influencing skills',
        'Bachelor\'s degree in related field; Master\'s preferred'
      ],
      qualifications: [
        'D&I certification (CDP, CCDP/AP) preferred',
        'Experience in global D&I program implementation',
        'Background in organizational change management',
        'Knowledge of unconscious bias and inclusive leadership frameworks'
      ],
      benefits: [
        'Comprehensive benefits with emphasis on wellness and mental health',
        'Flexible work arrangements and generous time off',
        'Professional development and D&I certification support',
        'Mentorship and sponsorship programs',
        'Community engagement and volunteer time off',
        'Inclusion-focused team building events'
      ],
      applications: 19,
      views: 156,
      urgent: true,
      featured: false,
      companyInfo: {
        size: '1000-2000 employees',
        industry: 'Healthcare Technology',
        founded: 2012,
        mission: 'To create healthcare solutions that serve diverse populations with equity and compassion'
      }
    }
  ];

  // Filter and sort jobs
  const filteredJobs = jobListings
    .filter(job => {
      const matchesSearch = searchTerm === '' || 
        job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.description.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesFilters = 
        (filters.department === '' || job.department === filters.department) &&
        (filters.experience === '' || job.experience === filters.experience) &&
        (filters.location === '' || job.location.includes(filters.location)) &&
        (filters.jobType === '' || job.type === filters.jobType);

      return matchesSearch && matchesFilters;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.posted) - new Date(a.posted);
        case 'salary':
          const salaryA = parseInt(a.salary.replace(/[^\d]/g, ''));
          const salaryB = parseInt(b.salary.replace(/[^\d]/g, ''));
          return salaryB - salaryA;
        case 'applications':
          return b.applications - a.applications;
        default:
          return 0;
      }
    });

  const handleSaveJob = (jobId) => {
    if (savedJobs.includes(jobId)) {
      setSavedJobs(savedJobs.filter(id => id !== jobId));
    } else {
      setSavedJobs([...savedJobs, jobId]);
    }
  };

  const handleQuickApply = (job) => {
    // Simulate quick apply functionality
    alert(`Quick application submitted for ${job.title} at ${job.company}`);
  };

  const JobDetailModal = ({ job, onClose }) => (
    <div className="jobportal__modal-overlay" onClick={onClose}>
      <div className="jobportal__modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="jobportal__modal-close" onClick={onClose}>
          <FaTimes />
        </button>

        <div className="jobportal__modal-header">
          <div className="jobportal__modal-company">
            <div className="jobportal__company-logo">{job.logo}</div>
            <div className="jobportal__company-info">
              <h2 className="jobportal__modal-title">{job.title}</h2>
              <p className="jobportal__company-name">{job.company}</p>
              <div className="jobportal__job-meta">
                <span><FaMapMarkerAlt /> {job.location}</span>
                <span><FaBriefcase /> {job.department}</span>
                <span><FaDollarSign /> {job.salary}</span>
                <span><FaClock /> {job.type}</span>
              </div>
            </div>
          </div>
          <div className="jobportal__modal-actions">
            <button 
              className={`jobportal__save-btn ${savedJobs.includes(job.id) ? 'jobportal__save-btn--saved' : ''}`}
              onClick={() => handleSaveJob(job.id)}
            >
              <FaHeart />
              {savedJobs.includes(job.id) ? 'Saved' : 'Save'}
            </button>
            <button className="jobportal__apply-btn" onClick={() => handleQuickApply(job)}>
              Quick Apply
            </button>
          </div>
        </div>

        <div className="jobportal__modal-body">
          {/* Company Information */}
          <div className="jobportal__modal-section">
            <h3 className="jobportal__section-title">
              <FaBuilding /> About {job.company}
            </h3>
            <div className="jobportal__company-details">
              <div className="jobportal__company-stats">
                <div className="jobportal__company-stat">
                  <strong>Industry:</strong> {job.companyInfo.industry}
                </div>
                <div className="jobportal__company-stat">
                  <strong>Company Size:</strong> {job.companyInfo.size}
                </div>
                <div className="jobportal__company-stat">
                  <strong>Founded:</strong> {job.companyInfo.founded}
                </div>
              </div>
              <div className="jobportal__company-mission">
                <strong>Mission:</strong> {job.companyInfo.mission}
              </div>
            </div>
          </div>

          {/* Job Overview */}
          <div className="jobportal__modal-section">
            <h3 className="jobportal__section-title">
              <FaRocket /> Role Overview
            </h3>
            <p className="jobportal__job-overview">{job.overview}</p>
          </div>

          {/* Job Description */}
          <div className="jobportal__modal-section">
            <h3 className="jobportal__section-title">
              <FaUserTie /> Position Description
            </h3>
            <p className="jobportal__job-description">{job.description}</p>
          </div>

          {/* Key Responsibilities */}
          <div className="jobportal__modal-section">
            <h3 className="jobportal__section-title">
              <FaTasks /> Key Responsibilities
            </h3>
            <ul className="jobportal__requirements-list">
              {job.responsibilities.map((resp, index) => (
                <li key={index} className="jobportal__requirement-item">
                  <FaCheck className="jobportal__check-icon" />
                  {resp}
                </li>
              ))}
            </ul>
          </div>

          {/* Requirements */}
          <div className="jobportal__modal-section">
            <h3 className="jobportal__section-title">
              <FaGraduationCap /> Required Qualifications
            </h3>
            <ul className="jobportal__requirements-list">
              {job.requirements.map((req, index) => (
                <li key={index} className="jobportal__requirement-item">
                  <FaCheck className="jobportal__check-icon" />
                  {req}
                </li>
              ))}
            </ul>
          </div>

          {/* Preferred Qualifications */}
          {job.qualifications && job.qualifications.length > 0 && (
            <div className="jobportal__modal-section">
              <h3 className="jobportal__section-title">
                <FaAward /> Preferred Qualifications
              </h3>
              <ul className="jobportal__requirements-list">
                {job.qualifications.map((qual, index) => (
                  <li key={index} className="jobportal__requirement-item">
                    <FaStar className="jobportal__check-icon" />
                    {qual}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Benefits & Perks */}
          <div className="jobportal__modal-section">
            <h3 className="jobportal__section-title">
              <FaHandshake /> Compensation & Benefits
            </h3>
            <div className="jobportal__benefits-grid">
              {job.benefits.map((benefit, index) => (
                <div key={index} className="jobportal__benefit-card">
                  <FaStar className="jobportal__benefit-icon" />
                  <span>{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Job Statistics */}
          <div className="jobportal__modal-section">
            <h3 className="jobportal__section-title">
              <FaChartLine /> Position Statistics
            </h3>
            <div className="jobportal__stats-grid">
              <div className="jobportal__stat-item">
                <FaEye className="jobportal__stat-icon" />
                <div>
                  <span className="jobportal__stat-number">{job.views}</span>
                  <span className="jobportal__stat-label">Total Views</span>
                </div>
              </div>
              <div className="jobportal__stat-item">
                <FaUsers className="jobportal__stat-icon" />
                <div>
                  <span className="jobportal__stat-number">{job.applications}</span>
                  <span className="jobportal__stat-label">Applications</span>
                </div>
              </div>
              <div className="jobportal__stat-item">
                <FaCalendarAlt className="jobportal__stat-icon" />
                <div>
                  <span className="jobportal__stat-number">{job.posted}</span>
                  <span className="jobportal__stat-label">Time Posted</span>
                </div>
              </div>
              <div className="jobportal__stat-item">
                <FaBalanceScale className="jobportal__stat-icon" />
                <div>
                  <span className="jobportal__stat-number">
                    {Math.round((job.applications / job.views) * 100)}%
                  </span>
                  <span className="jobportal__stat-label">Apply Rate</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="jobportal__modal-footer">
          <div className="jobportal__footer-actions">
            <button className="jobportal__secondary-btn">
              <FaShare /> Share This Position
            </button>
            <div className="jobportal__primary-actions">
              <button className="jobportal__secondary-btn">
                <FaEnvelope /> Save for Later
              </button>
              <button className="jobportal__primary-btn">
                <FaUsersCog /> Apply Now
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const FilterPanel = () => (
    <div className={`jobportal__filter-panel ${showFilters ? 'jobportal__filter-panel--open' : ''}`}>
      <div className="jobportal__filter-header">
        <h3>Advanced Filters</h3>
        <button onClick={() => setShowFilters(false)} className="jobportal__filter-close">
          <FaTimes />
        </button>
      </div>

      <div className="jobportal__filter-group">
        <label>HR Department</label>
        <select 
          value={filters.department} 
          onChange={(e) => setFilters({...filters, department: e.target.value})}
        >
          <option value="">All HR Departments</option>
          {departments.map(dept => (
            <option key={dept} value={dept}>{dept}</option>
          ))}
        </select>
      </div>

      <div className="jobportal__filter-group">
        <label>Experience Level</label>
        <select 
          value={filters.experience} 
          onChange={(e) => setFilters({...filters, experience: e.target.value})}
        >
          <option value="">All Experience Levels</option>
          <option value="Entry-level">Entry-level (0-2 years)</option>
          <option value="Mid-level">Mid-level (3-7 years)</option>
          <option value="Senior">Senior (8+ years)</option>
          <option value="Executive">Executive (12+ years)</option>
        </select>
      </div>

      <div className="jobportal__filter-group">
        <label>Salary Range</label>
        <select 
          value={filters.salary} 
          onChange={(e) => setFilters({...filters, salary: e.target.value})}
        >
          <option value="">Any Salary</option>
          <option value="60000">$60,000+</option>
          <option value="80000">$80,000+</option>
          <option value="100000">$100,000+</option>
          <option value="120000">$120,000+</option>
          <option value="150000">$150,000+</option>
        </select>
      </div>

      <div className="jobportal__filter-group">
        <label>Location Preference</label>
        <select 
          value={filters.location} 
          onChange={(e) => setFilters({...filters, location: e.target.value})}
        >
          <option value="">All Locations</option>
          <option value="Remote">Remote Positions</option>
          <option value="New York">New York, NY</option>
          <option value="San Francisco">San Francisco, CA</option>
          <option value="Chicago">Chicago, IL</option>
          <option value="Austin">Austin, TX</option>
          <option value="Boston">Boston, MA</option>
        </select>
      </div>

      <div className="jobportal__filter-group">
        <label>Employment Type</label>
        <select 
          value={filters.jobType} 
          onChange={(e) => setFilters({...filters, jobType: e.target.value})}
        >
          <option value="">All Employment Types</option>
          <option value="Full-time">Full-time</option>
          <option value="Part-time">Part-time</option>
          <option value="Contract">Contract</option>
          <option value="Remote">Remote</option>
          <option value="Hybrid">Hybrid</option>
        </select>
      </div>

      <button 
        className="jobportal__clear-filters"
        onClick={() => setFilters({
          department: '',
          experience: '',
          salary: '',
          location: '',
          jobType: ''
        })}
      >
        Clear All Filters
      </button>
    </div>
  );

  return (
    <div className="jobportal">
      {/* Job Detail Modal */}
      {selectedJob && (
        <JobDetailModal job={selectedJob} onClose={() => setSelectedJob(null)} />
      )}

      {/* Filter Panel */}
      <FilterPanel />

      {/* Hero Section */}
      <section className="jobportal__hero">
        <div className="jobportal__container">
          <div className="jobportal__hero-content">
            <h1 className="jobportal__hero-title">
              HR Professional <span className="jobportal__highlight">Career Hub</span>
            </h1>
            <p className="jobportal__hero-subtitle">
              Discover exclusive HR career opportunities with leading organizations. 
              Connect with roles that match your expertise in human resources, talent management, 
              and organizational development.
            </p>
            
            {/* Search Bar */}
            <div className="jobportal__search">
              <div className="jobportal__search-container">
                <FaSearch className="jobportal__search-icon" />
                <input
                  type="text"
                  placeholder="Search HR roles, companies, or specialties (e.g., Talent Acquisition, HRBP, Compensation)..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="jobportal__search-input"
                />
                <button 
                  className="jobportal__filter-toggle"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <FaFilter />
                  Advanced Filters
                </button>
              </div>
            </div>

            {/* Professional Stats */}
            <div className="jobportal__quick-stats">
              <div className="jobportal__stat">
                <span className="jobportal__stat-number">{jobListings.length}+</span>
                <span className="jobportal__stat-label">HR Positions</span>
              </div>
              <div className="jobportal__stat">
                <span className="jobportal__stat-number">18</span>
                <span className="jobportal__stat-label">Top Companies</span>
              </div>
              <div className="jobportal__stat">
                <span className="jobportal__stat-number">94%</span>
                <span className="jobportal__stat-label">Match Rate</span>
              </div>
              <div className="jobportal__stat">
                <span className="jobportal__stat-number">48h</span>
                <span className="jobportal__stat-label">Avg. Response</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="jobportal__main">
        <div className="jobportal__container">
          <div className="jobportal__content-header">
            <div className="jobportal__header-content">
              <h2 className="jobportal__section-title">
                Strategic HR Opportunities
                <span className="jobportal__job-count">({filteredJobs.length} positions)</span>
              </h2>
              <p className="jobportal__section-subtitle">
                Curated HR roles from innovative companies seeking experienced human resources professionals
              </p>
            </div>
            
            <div className="jobportal__sort-controls">
              <span>Sort by:</span>
              <select 
                value={sortBy} 
                onChange={(e) => setSortBy(e.target.value)}
                className="jobportal__sort-select"
              >
                <option value="newest">Most Recent</option>
                <option value="salary">Highest Salary</option>
                <option value="applications">Most Popular</option>
              </select>
            </div>
          </div>

          {/* Jobs Grid */}
          <div className="jobportal__jobs-grid">
            {filteredJobs.map(job => (
              <article 
                key={job.id} 
                className={`jobportal__job-card ${job.featured ? 'jobportal__job-card--featured' : ''} ${job.urgent ? 'jobportal__job-card--urgent' : ''}`}
                onClick={() => setSelectedJob(job)}
              >
                {job.featured && (
                  <div className="jobportal__featured-badge">
                    <FaStar /> Featured
                  </div>
                )}
                {job.urgent && (
                  <div className="jobportal__urgent-badge">
                    <FaRocket /> Urgent Hire
                  </div>
                )}

                <div className="jobportal__job-header">
                  <div className="jobportal__company-logo">{job.logo}</div>
                  <div className="jobportal__job-basic">
                    <h3 className="jobportal__job-title">{job.title}</h3>
                    <p className="jobportal__company-name">{job.company}</p>
                    <div className="jobportal__experience-badge">
                      {job.experience} Level
                    </div>
                  </div>
                  <button 
                    className={`jobportal__save-icon ${savedJobs.includes(job.id) ? 'jobportal__save-icon--saved' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSaveJob(job.id);
                    }}
                  >
                    <FaHeart />
                  </button>
                </div>

                <div className="jobportal__job-meta">
                  <span className="jobportal__meta-item">
                    <FaMapMarkerAlt />
                    {job.location}
                  </span>
                  <span className="jobportal__meta-item">
                    <FaBriefcase />
                    {job.department}
                  </span>
                  <span className="jobportal__meta-item">
                    <FaDollarSign />
                    {job.salary}
                  </span>
                </div>

                <p className="jobportal__job-excerpt">{job.description}</p>

                <div className="jobportal__job-footer">
                  <div className="jobportal__job-stats">
                    <span className="jobportal__stat">
                      <FaEye /> {job.views} views
                    </span>
                    <span className="jobportal__stat">
                      <FaUsers /> {job.applications} applicants
                    </span>
                    <span className="jobportal__stat">
                      <FaClock /> {job.posted}
                    </span>
                  </div>
                  <button className="jobportal__view-job">
                    View Details <FaArrowRight />
                  </button>
                </div>
              </article>
            ))}
          </div>

          {filteredJobs.length === 0 && (
            <div className="jobportal__no-results">
              <div className="jobportal__no-results-icon">🔍</div>
              <h3>No HR positions match your criteria</h3>
              <p>Try broadening your search terms or adjust your filters to discover more opportunities.</p>
              <button 
                className="jobportal__clear-search"
                onClick={() => {
                  setSearchTerm('');
                  setFilters({
                    department: '',
                    experience: '',
                    salary: '',
                    location: '',
                    jobType: ''
                  });
                }}
              >
                Reset Search & Filters
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Professional CTA Section */}
      <section className="jobportal__cta">
        <div className="jobportal__container">
          <div className="jobportal__cta-content">
            <h2>Advance Your HR Career Journey</h2>
            <p>
              Join thousands of HR professionals who have accelerated their careers through our specialized platform. 
              Access exclusive opportunities, salary insights, and career development resources tailored for human resources experts.
            </p>
            <div className="jobportal__cta-buttons">
              <button className="jobportal__cta-btn jobportal__cta-btn--primary">
                <FaUserTie /> Upload HR Resume
              </button>
              <button className="jobportal__cta-btn jobportal__cta-btn--secondary">
                <FaChartLine /> Receive HR Job Alerts
              </button>
              <button className="jobportal__cta-btn jobportal__cta-btn--secondary">
                <FaHandshake /> HR Career Consultation
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HRJobBoard;